import { React, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import "./index.scss";

import ProfileAvatar from "../../../../components/ui/ProfileAvatar";

import ScanModal from "../../Domain/ScanModal";

import { domainTileViewScanStartIcon } from "../../../../utils/icons";
import { formatDate, formatNumber, getDomainImageUrl } from "../../../../utils/util";
import SkeletonLoader from "../../../ui/SkeletonLoader";


const DomainListView = ({ domainList, isLoading }) => {

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [scanningDomain, setScanningDomain] = useState(null);
    const [showStartScanModal, setShowStartScanModal] = useState(false); // Modal visibility state

    const handleStartScan = (data) => {
        setScanningDomain(data);
        setShowStartScanModal(true); // Open the modal
    };

    const handleCloseModal = () => setShowStartScanModal(false); // Close modal function

    const handleViewDetailsClick = (data) => {
        navigate(`/domain/details/${data.domain}`); // Replace '/details' with your target route
    };

    const formatCreatedDate = (data) => {
        let date = formatDate(data.created_at)
        return `Created On : ${date}`
    }

    return (
        <Row>
            <Col md="12">
                {
                    isLoading ?
                        <div className="domain-list-view-container organization-list-view-table p-3 mt-4">
                            <SkeletonLoader type="line" count={5} currentPage={"domainList"} />
                        </div>              
                    :
                    <div className="domain-list-view-container organization-list-view-table p-3 mt-4">
                        <Table responsive="lg">
                            <thead>
                                <tr>
                                    <th>{t('domainPage.listView.domain')}</th>
                                    <th>{t('domainPage.listView.scanStatus')}</th>
                                    <th>{t('domainPage.listView.hackerScore')}</th>
                                    <th>{t('domainPage.listView.riskRating')}</th>
                                    <th>{t('domainPage.listView.totalFinding')}</th>
                                    <th>{t('domainPage.listView.lastScan')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    domainList?.results.length > 0 ?
                                    domainList?.results.map((data, index)=>{
                                        return (
                                            <motion.tr
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                // whileInView={{ opacity: 1, y: 0 }}
                                                // viewport={{ once: true }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                            >
                                                <td>
                                                    <ProfileAvatar type="domain" name={data?.domain} imageUrl={getDomainImageUrl(data)} secondaryText={formatCreatedDate(data)}/>
                                                </td>
                                                <td className="domain-list-view-scan-status">
                                                    {data?.scan_queue_detail?.scan_status === "completed" && <div className="scan-progress">
                                                        <div className="completed"> 
                                                            <div>Completed</div> 
                                                        </div>
                                                    </div>}
                                                    {data?.scan_queue_detail?.scan_status === "new" && <div className="scan-progress">
                                                        <div className="new"> 
                                                            <div>New</div> 
                                                        </div>
                                                    </div>}
                                                    {data?.scan_queue_detail?.scan_status === "pending" && <div className="scan-progress queued">
                                                        <h4>Pending - {data?.scan_queue_detail?.scan_percentage}%</h4>
                                                        <div className="progress-container"> 
                                                            <div style={{width:`${data?.scan_queue_detail?.scan_percentage}%`}}></div> 
                                                        </div>
                                                    </div>}
                                                    {data?.scan_queue_detail?.scan_status === "stopped" && <div className="scan-progress queued">
                                                        <h4>Stopped - {data?.scan_queue_detail?.scan_percentage}%</h4>
                                                        <div className="progress-container"> 
                                                            <div style={{width:`${data?.scan_queue_detail?.scan_percentage}%`}}></div> 
                                                        </div>
                                                    </div>}
                                                    {data?.scan_queue_detail?.scan_status === "in_progress" && <div className="scan-progress inprogress">
                                                        <h4>{data?.scan_queue_detail?.scan_percentage}%</h4>
                                                        <div className="progress-container"> 
                                                            <div style={{width:`${data?.scan_queue_detail?.scan_percentage}%`}}></div> 
                                                        </div>
                                                    </div>}
                                                </td>
                                                <td>
                                                    <p>{data?.stats?.score?.latest_score?.overall_score?.obtained_score}</p>
                                                </td>
                                                <td>
                                                    <div className="finding-list">
                                                        <ul type="none" className="m-0 p-0">
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("domainPage.infrastructure")}</span>
                                                                <span>{data?.stats?.score?.latest_score?.infrastructure_score?.obtained_rating}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("domainPage.vulnerabilities")}</span>
                                                                <span>{data?.stats?.score?.latest_score?.vulnerability_score?.obtained_rating}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("domainPage.exposure")}</span>
                                                                <span>{data?.stats?.score?.latest_score?.exposure_score?.obtained_rating}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("domainPage.darkWeb")}</span>
                                                                <span>{data?.stats?.score?.latest_score?.darkweb_score?.obtained_rating}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="finding-list">
                                                        <ul type="none" className="m-0 p-0">
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("organizationPage.assetDiscovery")}</span>
                                                                <span>{formatNumber(data?.stats?.new_findings?.asset_discovery)}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("organizationPage.externalDetectedVulnerabilities")}</span>
                                                                <span>{formatNumber(data?.stats?.new_findings?.external_detected_vulnerabilities)}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("organizationPage.DarkWeb")}</span>
                                                                <span>{formatNumber(data?.stats?.new_findings?.darkweb)}</span>
                                                            </li>
                                                            <li className="d-flex justify-content-between">
                                                                <span>{t("organizationPage.concerningExposures")}</span>
                                                                <span>{formatNumber(data?.stats?.new_findings?.concerning_exposures)}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                                <td>
                                                    <p>{data?.last_scan_detail?.created_at != null ? formatDate(data?.last_scan_detail?.created_at) : "-"}</p>
                                                </td>
                                                <td>
                                                    <div className='organization-list-view'>
                                                        <button className="view-details-btn-scan" disabled={data?.scan_queue_detail?.scan_status !== "new" && data?.scan_queue_detail?.scan_status !== "completed"} 
                                                            onClick={() => handleStartScan(data)}>
                                                                {domainTileViewScanStartIcon()}
                                                                {t("domainPage.startScanBtn")}
                                                        </button>
                                                        <button className="view-scan-btn" onClick={()=>{handleViewDetailsClick(data)}}>
                                                            {t("organizationPage.viewDetailsBtn")}
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        )
                                    }): ""
                                }                            
                            </tbody>
                        </Table>
                        <ScanModal show={showStartScanModal} onHide={handleCloseModal} domainData={scanningDomain}/>
                    </div>
                }                
            </Col>
        </Row>
    )
}

export default DomainListView;